import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import useEventsPack from '../events/useEventsPack';

const AREAS = [
    ['financial', 'Financial'],
    ['dispatch', 'Dispatch'],
    ['events', 'Events'],
    ['personnel', 'Personnel'],
    ['support_access', 'Support access'],
    ['crm', 'CRM'],
    ['comms', 'Communications'],
    ['tasks', 'Tasks'],
    ['reports', 'Reports'],
    ['settings', 'Settings'],
];
const LEVELS = ['off', 'view', 'edit', 'manage'];

// G8: "Events" is its own area, listed only for an organization that has the Events pack on (everyone else never sees it).
export function areasFor(eventsOn) {
    return AREAS.filter(([key]) => key !== 'events' || eventsOn);
}

function emptyLevels(areas) {
    return Object.fromEntries(areas.map(([key]) => [key, 'off']));
}

function presetKey(name) {
    return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

export default function RolePresetsAdminPanel({ userId, theme = 'light', areaFilter = '' }) {
    const dark = theme === 'dark';
    const { enabled: eventsOn } = useEventsPack(userId);
    const allAreas = areasFor(eventsOn);
    // a filter (the Events Settings shortcut) shows just that area's column; an area that is not offered shows everything
    const areas = areaFilter && allAreas.some(([key]) => key === areaFilter) ? allAreas.filter(([key]) => key === areaFilter) : allAreas;
    const [orgId, setOrgId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [presets, setPresets] = useState([]);
    const [groups, setGroups] = useState([]);
    const [groupPresets, setGroupPresets] = useState({});
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [orgResult, adminResult] = await Promise.all([
                supabaseClient.rpc('auth_org_id'),
                supabaseClient.rpc('is_org_admin'),
            ]);
            if (orgResult.error) throw orgResult.error;
            if (adminResult.error) throw adminResult.error;
            const currentOrgId = orgResult.data;
            setOrgId(currentOrgId);
            const admin = adminResult.data === true;
            setIsAdmin(admin);
            if (!currentOrgId || !admin) {
                setPresets([]);
                setGroups([]);
                setGroupPresets({});
                return;
            }
            const [presetResult, groupResult, assignmentResult] = await Promise.all([
                supabaseClient.from('org_role_presets')
                    .select('id, org_id, key, name, is_system, area_levels, show_trip_prices')
                    .eq('org_id', currentOrgId)
                    .order('name'),
                supabaseClient.from('org_groups')
                    .select('id, org_id, entity_id, parent_group_id, group_type, name')
                    .eq('org_id', currentOrgId)
                    .order('name'),
                supabaseClient.from('role_assignments')
                    .select('group_id, role_preset_id')
                    .eq('org_id', currentOrgId)
                    .in('grantee_type', ['team', 'group'])
                    .not('role_preset_id', 'is', null),
            ]);
            if (presetResult.error) throw presetResult.error;
            if (groupResult.error) throw groupResult.error;
            if (assignmentResult.error) throw assignmentResult.error;
            const assigned = {};
            (assignmentResult.data || []).forEach(({ group_id, role_preset_id }) => {
                if (group_id && role_preset_id) assigned[group_id] = role_preset_id;
            });
            setPresets(presetResult.data || []);
            setGroups(groupResult.data || []);
            setGroupPresets(assigned);
        } catch (loadError) {
            setError(loadError.message || 'Role presets could not be loaded.');
            setPresets([]);
            setGroups([]);
            setGroupPresets({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, userId]);

    const updatePreset = async (preset, changes) => {
        setSaving(preset.id);
        setError('');
        setNotice('');
        const { error: updateError } = await supabaseClient
            .from('org_role_presets')
            .update({ ...changes, updated_by: userId })
            .eq('id', preset.id)
            .eq('org_id', preset.org_id);
        if (updateError) setError(updateError.message);
        else {
            setPresets((current) => current.map((item) =>
                item.id === preset.id ? { ...item, ...changes } : item
            ));
            setNotice(`${preset.name} was updated.`);
        }
        setSaving('');
    };

    const createPreset = async (event) => {
        event.preventDefault();
        const cleanName = name.trim();
        const key = presetKey(cleanName);
        if (!cleanName || !key || !orgId) return;
        setSaving('new');
        setError('');
        setNotice('');
        const { data, error: insertError } = await supabaseClient
            .from('org_role_presets')
            .insert({
                org_id: orgId,
                key,
                name: cleanName,
                is_system: false,
                area_levels: emptyLevels(allAreas),
                show_trip_prices: false,
                created_by: userId,
                updated_by: userId,
            })
            .select('id, org_id, key, name, is_system, area_levels, show_trip_prices')
            .single();
        if (insertError) setError(insertError.message);
        else {
            setPresets((current) => [...current, data].sort((a, b) => a.name.localeCompare(b.name)));
            setName('');
            setNotice(`${cleanName} was created with all areas off.`);
        }
        setSaving('');
    };

    const assignGroupPreset = async (groupId, presetId) => {
        setSaving(groupId);
        setError('');
        setNotice('');
        const { error: assignError } = await supabaseClient.rpc('bl395a_assign_group_preset', {
            p_group_id: groupId,
            p_preset_id: presetId || null,
        });
        if (assignError) setError(assignError.message);
        else {
            setGroupPresets((current) => ({ ...current, [groupId]: presetId || null }));
            const group = groups.find((item) => item.id === groupId);
            setNotice(`${group?.name || 'Group'} default access was updated.`);
        }
        setSaving('');
    };

    const card = `rounded-xl border ${dark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`;
    const muted = dark ? 'text-gray-400' : 'text-gray-600';
    const input = `rounded-lg border px-3 py-2 ${dark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`;

    if (!loading && !isAdmin) return null;

    return (
        <section className="mt-6 space-y-4" aria-label="Role presets and groups">
            <div>
                <h3 className="text-xl font-semibold">Roles, Teams &amp; Groups</h3>
                <p className={`mt-1 text-sm ${muted}`}>
                    Presets are starting points for area access. Team and Group defaults flow down to their Departments.
                </p>
            </div>
            {error && <p role="alert" className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
            {notice && <p role="status" className="rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">{notice}</p>}
            {loading ? <p className={muted}>Loading role presets and groups…</p> : (
                <>
                    <div className={`${card} overflow-x-auto`}>
                        <div className="border-b border-inherit px-5 py-4">
                            <h4 className="font-semibold">Role presets</h4>
                            <p className={`mt-1 text-sm ${muted}`}>Edit each area as off, view, edit, or manage. System presets can be customized as starting points.</p>
                        </div>
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                    <th className="px-4 py-3 text-left">Preset</th>
                                    {areas.map(([key, label]) => <th key={key} className="px-2 py-3 text-left">{label}</th>)}
                                    <th className="px-4 py-3 text-left">Trip prices</th>
                                </tr>
                            </thead>
                            <tbody>
                                {presets.map((preset) => (
                                    <tr key={preset.id} className="border-t border-inherit">
                                        <td className="px-4 py-3 font-medium">
                                            {preset.name}
                                            {preset.is_system && <span className={`ml-2 text-xs ${muted}`}>(starting preset)</span>}
                                        </td>
                                        {areas.map(([key, label]) => (
                                            <td key={key} className="px-2 py-3">
                                                <label className="sr-only" htmlFor={`${preset.id}-${key}`}>{preset.name} {label} access</label>
                                                <select
                                                    id={`${preset.id}-${key}`}
                                                    aria-label={`${preset.name} ${label} access`}
                                                    className={input}
                                                    value={preset.area_levels?.[key] || 'off'}
                                                    disabled={!isAdmin || saving === preset.id}
                                                    onChange={(event) => updatePreset(preset, {
                                                        area_levels: { ...preset.area_levels, [key]: event.target.value },
                                                    })}
                                                >
                                                    {LEVELS.map((level) => <option key={level} value={level}>{level}</option>)}
                                                </select>
                                            </td>
                                        ))}
                                        <td className="px-4 py-3">
                                            <label className="inline-flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`${preset.name} show trip prices`}
                                                    checked={preset.show_trip_prices === true}
                                                    disabled={!isAdmin || saving === preset.id}
                                                    onChange={(event) => updatePreset(preset, { show_trip_prices: event.target.checked })}
                                                />
                                                Show
                                            </label>
                                        </td>
                                    </tr>
                                ))}
                                {presets.length === 0 && <tr><td colSpan={areas.length + 2} className={`px-4 py-6 ${muted}`}>No presets are configured for this organization.</td></tr>}
                            </tbody>
                        </table>
                        {isAdmin && (
                            <form onSubmit={createPreset} className="flex flex-wrap items-end gap-3 border-t border-inherit p-4">
                                <label className="text-sm font-medium">
                                    New preset
                                    <input className={`${input} ml-2`} value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required />
                                </label>
                                <button className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50" disabled={saving === 'new' || !presetKey(name)}>
                                    {saving === 'new' ? 'Creating…' : 'Create preset'}
                                </button>
                            </form>
                        )}
                    </div>

                    <div className={`${card} overflow-x-auto`}>
                        <div className="border-b border-inherit px-5 py-4">
                            <h4 className="font-semibold">Team &amp; Group defaults</h4>
                            <p className={`mt-1 text-sm ${muted}`}>Staff assigned to a Department inherit the nearest Team or Group preset. User-specific presets can override that default.</p>
                        </div>
                        {groups.length === 0 ? <p className={`px-5 py-4 text-sm ${muted}`}>No Teams, Groups, or Departments are configured.</p> : (
                            <table className="min-w-full text-sm">
                                <thead><tr className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                    <th className="px-4 py-3 text-left">Team / Group / Department</th>
                                    <th className="px-4 py-3 text-left">Default role preset</th>
                                </tr></thead>
                                <tbody>{groups.map((group) => {
                                    const type = (group.group_type || 'Department').toLowerCase();
                                    const canHaveDefault = type === 'team' || type === 'group';
                                    return <tr key={group.id} className="border-t border-inherit">
                                        <td className="px-4 py-3">{group.name} <span className={muted}>({group.group_type || 'Department'})</span></td>
                                        <td className="px-4 py-3">
                                            {canHaveDefault ? <select
                                                aria-label={`${group.name} role preset`}
                                                className={input}
                                                value={groupPresets[group.id] || ''}
                                                disabled={!isAdmin || saving === group.id}
                                                onChange={(event) => assignGroupPreset(group.id, event.target.value)}
                                            >
                                                <option value="">Inherit parent / no default</option>
                                                {presets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}
                                            </select> : <span className={muted}>Inherits nearest Team or Group</span>}
                                        </td>
                                    </tr>;
                                })}</tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </section>
    );
}
